// Package Imports
import { createRootRoute, createRouter } from '@tanstack/react-router'

// Core Imports
import Root from '@/core/pages/root.page'
import GlobalError from '@/core/pages/global-error.page'
import GlobalNotFound from '@/core/pages/global-not-found.page'

// Module Imports
import {
  authRoute,
  signInRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  updateEmailVerifyRoute,
} from '@/modules/auth/auth.routes'
import {
  categoryListRoute,
  categoryRoute,
} from '@/modules/category/category.routes'
import {
  dashboardRoute,
  forbiddenRoute,
  homeRoute,
} from '@/modules/dashboard/dashboard.routes'
import {
  avatarCreateRoute,
  avatarEditRoute,
  avatarListRoute,
  avatarRoute,
} from '@/modules/avatar/avatar.routes'
import {
  teamLogoCreateRoute,
  teamLogoEditRoute,
  teamLogoListRoute,
  teamLogoRoute,
} from '@/modules/team-logo/team-logo.routes'
import {
  emoteCreateRoute,
  emoteEditRoute,
  emoteListRoute,
  emoteRoute,
} from '@/modules/emote/emote.routes'
import {
  playerDetailRoute,
  playerEditRoute,
  playerListRoute,
  playerRoute,
} from '@/modules/player/player.routes'
import {
  questionCreateRoute,
  questionDetailRoute,
  questionEditRoute,
  questionListRoute,
  questionRoute,
} from '@/modules/question/question.routes'
import {
  questionPoolDetailRoute,
  questionPoolListRoute,
  questionPoolRoute,
} from '@/modules/question-pool/question-pool.routes'
import { profileRoute } from '@/modules/profile/profile.routes'

import {
  adminUsersCreateRoute,
  adminUsersDetailRoute,
  adminUsersEditRoute,
  adminUsersListRoute,
  adminUsersRoute,
} from '@/modules/admin/admin.routes'
import {
  rolesCreateRoute,
  rolesDetailRoute,
  rolesEditRoute,
  rolesListRoute,
  rolesRoute,
} from '@/modules/role/role.routes'
import {
  userRolesCreateRoute,
  userRolesDetailRoute,
  userRolesEditRoute,
  userRolesListRoute,
  userRolesRoute,
  userRolesAutoGrantsRoute,
} from '@/modules/user-role/user-role.routes'
import {
  teamDetailRoute,
  teamEditRoute,
  teamListRoute,
  teamLogoRequestsRoute,
  teamRoute,
} from '@/modules/team/team.routes'
import {
  lobbyRoute,
  lobbyListRoute,
  lobbyDetailRoute,
} from '@/modules/lobby/lobby.routes'
import {
  tournamentRoute,
  tournamentListRoute,
  tournamentCreateRoute,
  tournamentDetailRoute,
  tournamentEditRoute,
} from '@/modules/tournament/tournament.routes'
import {
  leagueRoute,
  leagueListRoute,
  leagueCreateRoute,
  leagueDetailRoute,
  leagueEditRoute,
} from '@/modules/league/league.routes'
import {
  customGameModeRoute,
  customGameModeListRoute,
  customGameModeCreateRoute,
  customGameModeEditRoute,
} from '@/modules/custom-game-mode/custom-game-mode.routes'
import {
  teamTagRoute,
  teamTagListRoute,
} from '@/modules/team-tag/team-tag.routes'
import {
  seasonRoute,
  seasonListRoute,
  seasonCreateRoute,
  seasonApplicationsRoute,
  seasonDetailRoute,
  seasonEditRoute,
} from '@/modules/season/season.routes'
import {
  individualAchievementRoute,
  individualAchievementListRoute,
  individualAchievementCreateRoute,
  individualAchievementDetailRoute,
  individualAchievementEditRoute,
} from '@/modules/individual-achievement/individual-achievement.routes'
import {
  teamAchievementRoute,
  teamAchievementListRoute,
  teamAchievementCreateRoute,
  teamAchievementDetailRoute,
  teamAchievementEditRoute,
} from '@/modules/team-achievement/team-achievement.routes'

export const rootRoute = createRootRoute({
  component: () => <Root />,
  errorComponent: ({ error }) => <GlobalError error={error} />,
  notFoundComponent: () => <GlobalNotFound />,
})

const routeTree = rootRoute.addChildren([
  authRoute.addChildren([
    signInRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    updateEmailVerifyRoute,
  ]),

  dashboardRoute.addChildren([homeRoute, forbiddenRoute, profileRoute]),

  categoryRoute.addChildren([categoryListRoute]),
  avatarRoute.addChildren([
    avatarListRoute,
    avatarCreateRoute,
    avatarEditRoute,
  ]),
  teamLogoRoute.addChildren([
    teamLogoListRoute,
    teamLogoCreateRoute,
    teamLogoEditRoute,
  ]),
  emoteRoute.addChildren([emoteListRoute, emoteCreateRoute, emoteEditRoute]),
  playerRoute.addChildren([
    playerListRoute,
    playerDetailRoute,
    playerEditRoute,
  ]),

  adminUsersRoute.addChildren([
    adminUsersListRoute,
    adminUsersCreateRoute,
    adminUsersDetailRoute,
    adminUsersEditRoute,
  ]),
  rolesRoute.addChildren([
    rolesListRoute,
    rolesCreateRoute,
    rolesDetailRoute,
    rolesEditRoute,
  ]),
  userRolesRoute.addChildren([
    userRolesListRoute,
    userRolesCreateRoute,
    userRolesDetailRoute,
    userRolesEditRoute,
    userRolesAutoGrantsRoute,
  ]),
  teamRoute.addChildren([
    teamListRoute,
    teamLogoRequestsRoute,
    teamDetailRoute,
    teamEditRoute,
  ]),

  questionRoute.addChildren([
    questionListRoute,
    questionDetailRoute,
    questionCreateRoute,
    questionEditRoute,
  ]),
  questionPoolRoute.addChildren([
    questionPoolListRoute,
    questionPoolDetailRoute,
  ]),

  lobbyRoute.addChildren([lobbyListRoute, lobbyDetailRoute]),
  tournamentRoute.addChildren([
    tournamentListRoute,
    tournamentCreateRoute,
    tournamentDetailRoute,
    tournamentEditRoute,
  ]),
  leagueRoute.addChildren([
    leagueListRoute,
    leagueCreateRoute,
    leagueDetailRoute,
    leagueEditRoute,
  ]),
  customGameModeRoute.addChildren([
    customGameModeListRoute,
    customGameModeCreateRoute,
    customGameModeEditRoute,
  ]),
  teamTagRoute.addChildren([teamTagListRoute]),
  seasonRoute.addChildren([
    seasonListRoute,
    seasonCreateRoute,
    seasonApplicationsRoute,
    seasonDetailRoute,
    seasonEditRoute,
  ]),
  individualAchievementRoute.addChildren([
    individualAchievementListRoute,
    individualAchievementCreateRoute,
    individualAchievementDetailRoute,
    individualAchievementEditRoute,
  ]),
  teamAchievementRoute.addChildren([
    teamAchievementListRoute,
    teamAchievementCreateRoute,
    teamAchievementDetailRoute,
    teamAchievementEditRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Suppress duplicate fetches when hovering nav after first preload.
  // Loader still re-runs after this window expires.
  defaultPreloadStaleTime: 30_000,
  scrollRestoration: true,
})
